import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
    Card,
} from "@/components/ui/card"
import advancedFormat from 'dayjs/plugin/advancedFormat'
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Api,
    is_shikhar_account
} from '@/lib/utils';
import { Issue } from '@/lib/interfaces';
import { useStore } from '@/store';
import { useShallow } from 'zustand/shallow';
import { Skeleton } from "@/components/ui/skeleton"
import EventTimeLine from "@/components/features/event-timeline"
import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { Button } from '@/components/ui/button';
import { CloseIssueDialog } from '@/components/features/issues/close-issue';
import { toast } from 'sonner';
import { SearchIssueDialog } from '@/components/features/issues/search-issue';
import { Search } from 'lucide-react';

dayjs.extend(advancedFormat)

const DisplayOrderAttributes = ({ fieldName, fieldValue, orderId }: { 
    fieldName: string, 
    fieldValue: string | undefined,
    orderId?: boolean
}) => {
    return <div>
        <p className='text-sm text-slate-500 font-medium'>{fieldName}</p>
        {fieldValue !== undefined ? orderId? <Link to="/orders/$orderId" params={{ orderId: fieldValue }}>
            <p className='font-medium text-sm ml underline text-blue-500 cursor-pointer'>{fieldValue || '--'}</p>
        </Link>:<p className='font-medium text-sm ml'>{fieldValue || '--'}</p> : <Skeleton className="h-4 w-[250px]" />}
    </div>
}

const getEventTimelines = (issue: Issue | undefined) => {
    let timelines: {
        name: string,
        timestamp: string,
        line3?: string,
        line4?: string,
        type?: 'red' | 'green'
    }[] = []
    if (issue) {
        if (issue.createdat) {
            timelines.push({
                name: 'Issue Raised',
                timestamp: issue.createdat
            })
        }

        if (issue.statusUpdatedat) {
            timelines.push({
                name: 'Status Updated',
                timestamp: issue.statusUpdatedat
            })
        }

        if (issue.relayedat) {
            timelines.push({
                name: 'Issue Relayed',
                timestamp: issue.relayedat
            })
        }

        if (issue.updatedat) {
            timelines.push({
                name: 'Issue Updated',
                timestamp: issue.updatedat
            })
        }

        if (issue.resolvedat) {
            timelines.push({
                name: 'Issue Resolved',
                timestamp: issue.resolvedat,
                type: 'green'
            })
        }

        if (issue.closedat) {
            timelines.push({
                name: 'Issue Closed',
                timestamp: issue.closedat,
            })
        }
    }

    return timelines
}

export const Route = createFileRoute('/_auth/issues/$issueId')({
    component: RouteComponent,
})


function RouteComponent() {
    const { issueId } = Route.useParams()
    const queryClient = useQueryClient()
    const [showSearchIssue, setShowSearchIssue] = useState(false)
    const [errMsg, setErrMsg] = useState<string | undefined>(undefined)
    const navigate = useNavigate({ from: Route.fullPath })

    const { token, setPage, accountId } = useStore(useShallow(state => ({
        token: state.token,
        setPage: state.setPage,
        accountId: state.selectedAccount.id
    })))

    useEffect(() => {
        setPage('Issues')
    }, [])

    const getIssueInfo = useQuery({
        queryKey: ['issueInfo', issueId],
        queryFn: () => Api<{ status: number, issue: Issue }>('/webui/issue/info', "POST", { issue_id: issueId.replace(/\s+/g, '') }, { token }),
        staleTime: 60 * 1000,
        refetchInterval: 90 * 1000,
        refetchOnWindowFocus: false,
        retry: 1
    })

    useEffect(() => {
        if (getIssueInfo.error) {
            setErrMsg('Error fetching issue details.')
            setShowSearchIssue(true)
        }
    }, [getIssueInfo.error])

    const refreshIssue = useMutation({
        mutationFn: () => {
            return Api<{ status: number, issue: Issue }>('/webui/order/issue_status', "POST",
                { issue: { id: issueId } }, { token })
        },
        onError(error) {
            toast.error(error.message ||  "Error refreshing issue")
        },
        onSuccess(data, _v, _c) {
            queryClient.setQueryData(['issueInfo', issueId], (oldData: { status: number, issue: Issue }) => {
                return {
                    ...oldData,
                    issue: {
                        ...oldData.issue,
                        ...data.issue,
                    }
                }
            })
            toast.success('Issue refreshed')
        }
    })

    const issue = getIssueInfo.data?.issue
    const isShikhar = is_shikhar_account(accountId)

    return <div className='w-full px-5 py-2 *:mb-6 md:px-20 lg:px-52'>
        <Card className='p-0'>
            <div>
                <div className='bg-gray-200 px-7 py-4 rounded-t-xl flex items-center gap-2'>
                    <p className='font-semibold text-lg'>Issue Details</p>
                    <Search className='w-5 h-5 cursor-pointer' strokeWidth={3} onClick={() => setShowSearchIssue(true)} />
                </div>
                <div className='py-6 px-4 lg:px-10 grid gap-6 grid-cols-1 lg:grid-cols-3 sm:grid-cols-2'>
                    <DisplayOrderAttributes fieldName='Issue Id' fieldValue={issue?.issueId} />
                    {!isShikhar ? <DisplayOrderAttributes fieldName='Network Order Id' fieldValue={issue?.networkOrderId} /> : null}
                    <DisplayOrderAttributes fieldName='Order Id' fieldValue={issue?.orderId} orderId={true} />
                    <DisplayOrderAttributes fieldName='Client Order Id' fieldValue={issue?.clientOrderId} />
                    {!isShikhar ? <DisplayOrderAttributes fieldName='Status' fieldValue={issue?.issueStatus} /> : null}
                    <DisplayOrderAttributes fieldName='Resolution Status' fieldValue={issue?.resolutionStatus} />
                    {!isShikhar ? <DisplayOrderAttributes fieldName='Category' fieldValue={issue?.category} /> : null}
                    {!isShikhar ? <DisplayOrderAttributes fieldName='Sub-Category' fieldValue={issue?.subCategory} /> : null}
                    <DisplayOrderAttributes fieldName='Resolution Action' fieldValue={issue?.resolutionAction} />
                </div>

                <div className='border mx-4 lg:mx-10 px-3 py-2 rounded-md relative mb-8 border-gray-100'>
                    <p className='absolute left-3 -top-3 bg-white text-sm text-slate-500 font-medium'>Short Description</p>
                    <p className='text-sm font-medium'>{issue?.shortDescription || '--'}</p>
                </div>

                <div className='border mx-4 lg:mx-10 px-3 py-2 rounded-md relative mb-8 border-gray-100'>
                    <p className='absolute left-3 -top-3 bg-white text-sm text-slate-500 font-medium'>Long Description</p>
                    <p className='text-sm font-medium'>{issue?.longDescription || '--'}</p>
                </div>

                <div className='border mx-4 lg:mx-10 px-3 py-2 rounded-md relative mb-8 border-gray-100'>
                    <p className='absolute left-3 -top-3 bg-white text-sm text-slate-500 font-medium'>Resolution Description</p>
                    <div className='text-sm font-medium overflow-auto'>{/<\/?\w+>/.test((issue?.resolutionDescription || '')) ? parse((issue?.resolutionDescription || '').replace(/\\u00a0/g, '<br/>')) : <p className='text-sm font-medium'>{issue?.resolutionDescription || '--'}</p>}</div>
                </div>
                {issue ? <div className={`flex w-full items-center rounded-md relative mb-8 lg:gap-6 gap-4 justify-start px-4 lg:px-10`}>
                    <CloseIssueDialog issueId={issue.issueId} issueDescription={issue.longDescription} onSuccess={() => getIssueInfo.refetch()}
                            resolutionAction={issue.resolutionAction} resolutionDescription={issue.resolutionDescription}>
                        <Button variant={'destructive'} disabled={issue.issueStatus === 'CLOSED'}>
                            Close Issue
                        </Button>
                    </CloseIssueDialog>

                    <Button variant={'blue'} onClick={() => refreshIssue.mutate()} loading={refreshIssue.isPending}>
                        Refresh Issue
                    </Button>
                </div> : null}
            </div>
        </Card>
        {!isShikhar ? (
            <Card className='p-0'>
                <div>
                    <div className='bg-gray-200 px-7 py-4 rounded-t-xl'>
                        <p className='font-semibold text-lg'>Timeline</p>
                    </div>
                    <div className='px-7'>
                        <EventTimeLine events={getEventTimelines(issue)} />
                    </div>
                </div>
            </Card>
        ) : null}
        {!isShikhar ? (
            <Card className='p-0'>
                <div>
                    <div className='bg-gray-200 px-7 py-4 rounded-t-xl'>
                        <p className='font-semibold text-lg'>Payment Details</p>
                    </div>
                    <div className='py-6 lg:px-40 md:px-10 px-5'>
                        <table className='w-full border-collapse [&_tr]:last:[&_td]:border-none  [&_td]:border-b [&_td]:p-2 [&_td]:last:text-right [&_td]:last:font-medium text-sm'>
                            <tr>
                                <td className='font-medium'>Description</td>
                                <td className='font-medium'>Amount</td>
                            </tr>
                            <tr>
                                <td>Refund amount</td>
                                <td>{`₹ ${issue?.refundAmount || 0}`}</td>
                            </tr>
                            <tr>
                                <td>Expected refund amount</td>
                                <td>{`₹ ${issue?.expectedRefundAmount || 0}`}</td>
                            </tr>
                            <tr>
                                <td>Issued refund amount</td>
                                <td>{`₹ ${issue?.issuedRefundAmount || 0}`}</td>
                            </tr>
                        </table>
                    </div>
                </div>
            </Card>
        ) : null}
        <SearchIssueDialog open={showSearchIssue} onClose={(resetSearch) => {
            setShowSearchIssue(false)
            if (resetSearch) {
                navigate({
                    search: () => ({
                        search: false
                    })
                })
            }
        }} errMsg={errMsg} clearErrMsg={() => setErrMsg(undefined)} searchedIssueId={issueId} />
    </div >
}
